import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Info, Play, Plus, X } from 'lucide-react';
import { useAuthHeader } from '@/hooks/useAuth';
import { getProductGroupTypes } from '@/lib/api/product-group-type';
import { getInstanceQuotas } from '@/lib/api/ds/prediction-error-evaluation';
import { createTargetSkusPresignedUpload } from '@/lib/api/ds/prediction-error-evaluation-upload';
import { uploadFileToS3 } from '@/lib/api/s3-upload';
import { parseSkuLines } from './parseSkuLines';
import { TargetSkuSection, isTargetSkusTextOverLimit } from './TargetSkuSection';
import { targetSkuUploadContentType } from './targetSkuFileRules';
import type { StartPredictionErrorEvaluationRequest } from '@/lib/types/ds/prediction-error-evaluation';
import type { InstanceQuota } from '@/lib/types/ds/prediction-error-evaluation';
import type { SimpleAccount } from '@/lib/types';

interface PredictionErrorEvaluationFormProps {
  accounts: SimpleAccount[];
  onSubmit: (accountCode: string, request: StartPredictionErrorEvaluationRequest) => void;
  isSubmitting: boolean;
}

const DEFAULT_TRAIN_TERMS = [14, 21, 28, 35, 56, 90];
const DEFAULT_TOLERANCES = [0.4, 0.5];
const DEFAULT_MAX_DATA_COUNTS = [7, 14];

const INSTANCE_TYPES = [
  { value: 'ml.m5.xlarge', label: 'ml.m5.xlarge（標準）' },
  { value: 'ml.r5.2xlarge', label: 'ml.r5.2xlarge（メモリ重視）' },
  { value: 'ml.r5.4xlarge', label: 'ml.r5.4xlarge（大規模データ向け）' },
];

export function PredictionErrorEvaluationForm({
  accounts,
  onSubmit,
  isSubmitting,
}: PredictionErrorEvaluationFormProps) {
  const { getAuthHeader } = useAuthHeader();
  const [selectedAccountCode, setSelectedAccountCode] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [targetDates, setTargetDates] = useState<string[]>(['']);
  const [evalDays, setEvalDays] = useState(14);
  const [targetSkusText, setTargetSkusText] = useState('');
  const [targetSkusFile, setTargetSkusFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [trainTerms, setTrainTerms] = useState(DEFAULT_TRAIN_TERMS.join(', '));
  const [tolerances, setTolerances] = useState(DEFAULT_TOLERANCES.join(', '));
  const [maxDataCounts, setMaxDataCounts] = useState(DEFAULT_MAX_DATA_COUNTS.join(', '));
  const [adjustEvent, setAdjustEvent] = useState(true);
  const [adjustNewSkus, setAdjustNewSkus] = useState(true);
  const [adjustNewSkusMinTrainTerm, setAdjustNewSkusMinTrainTerm] = useState(14);
  const [adjustNewSkusMaxDaysOnSales, setAdjustNewSkusMaxDaysOnSales] = useState(28);
  const [instanceType, setInstanceType] = useState('ml.m5.xlarge');
  const [productGroupTypeName, setProductGroupTypeName] = useState('');
  const [productGroupLayer, setProductGroupLayer] = useState(1);
  const [quotaOpen, setQuotaOpen] = useState(false);

  // アカウント選択時に商品グループタイプ一覧を取得
  const { data: productGroupTypes = [] } = useQuery({
    queryKey: ['productGroupTypes', selectedAccountCode],
    queryFn: () => getProductGroupTypes(selectedAccountCode, getAuthHeader()),
    enabled: !!selectedAccountCode,
  });

  // インスタンスクォータを事前取得（AWSアカウント単位）
  const { data: quotaData } = useQuery({
    queryKey: ['instanceQuotas'],
    queryFn: () => getInstanceQuotas(getAuthHeader()),
    staleTime: 5 * 60 * 1000,
  });
  const quotaMap = new Map<string, number>(
    (quotaData?.quotas ?? []).map((q: InstanceQuota) => [q.instance_type, q.quota]),
  );

  const handleAccountChange = (accountCode: string) => {
    setSelectedAccountCode(accountCode);
    setProductGroupTypeName('');
    const account = accounts.find((a) => a.code === accountCode);
    if (account) {
      setSelectedAccountId(account.id);
    }
  };

  const addTargetDate = () => {
    setTargetDates([...targetDates, '']);
  };

  const removeTargetDate = (index: number) => {
    setTargetDates(targetDates.filter((_, i) => i !== index));
  };

  const updateTargetDate = (index: number, value: string) => {
    const updated = [...targetDates];
    updated[index] = value;
    setTargetDates(updated);
  };

  const parseNumberArray = (value: string): number[] => {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '')
      .map(Number)
      .filter((n) => !isNaN(n));
  };

  // 入力が数百行になり得るため、テキストが変わったときだけパースする
  const parsedSkus = useMemo(() => parseSkuLines(targetSkusText), [targetSkusText]);

  /**
   * 対象SKU CSV を S3 へ直接アップロードし、ジョブ起動に渡す S3 キーを返す。
   *
   * API Gateway のペイロード上限 10MB（引き上げ不可）を超えるため、presigned URL を
   * 取得して S3 へ直接 PUT する。失敗したら null を返し、ジョブは起動させない
   * （存在しないキーで起動すると Fargate まで行って初めて失敗する）。
   */
  const uploadTargetSkusFile = async (file: File): Promise<string | null> => {
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);
    try {
      const presigned = await createTargetSkusPresignedUpload(
        selectedAccountCode,
        file.name,
        file.size,
        getAuthHeader(),
      );
      // Content-Type は presigned URL の署名対象。BE が拡張子から導出した値と
      // 完全一致させる必要がある（File.type を送るとブラウザ依存で 403 になる）
      await uploadFileToS3(
        file,
        presigned.upload_url,
        setUploadProgress,
        targetSkuUploadContentType(file.name),
      );
      // s3_key は `s3://` を含まない生のキー。そのまま start_job に渡す
      return presigned.s3_key;
    } catch (e) {
      setUploadError(
        `対象SKUファイルのアップロードに失敗しました: ${e instanceof Error ? e.message : '不明なエラー'}`,
      );
      return null;
    } finally {
      setUploadProgress(null);
      // 失敗してもやり直せるように、送信ボタンは必ず戻す
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validDates = targetDates.filter((d) => d !== '');
    if (validDates.length === 0) return;
    if (!selectedAccountCode || !selectedAccountId) return;

    // ファイル経路。S3 への PUT が成功してから start_job を呼ぶ
    let targetSkusFilePath: string | undefined;
    if (targetSkusFile !== null) {
      const s3Key = await uploadTargetSkusFile(targetSkusFile);
      if (s3Key === null) return;
      targetSkusFilePath = s3Key;
    }

    const request: StartPredictionErrorEvaluationRequest = {
      account_id: selectedAccountId,
      target_dates: validDates,
      eval_days: evalDays,
      train_terms: parseNumberArray(trainTerms),
      tolerances: parseNumberArray(tolerances),
      max_data_counts: parseNumberArray(maxDataCounts),
      adjust_event: adjustEvent,
      adjust_new_skus: adjustNewSkus,
      adjust_new_skus_min_train_term: adjustNewSkusMinTrainTerm,
      adjust_new_skus_max_days_on_sales: adjustNewSkusMaxDaysOnSales,
      product_group_type_name: productGroupTypeName || undefined,
      product_group_layer: productGroupLayer,
      instance_type: instanceType,
      // 空配列ではなく未指定で送る（バックエンドは未指定を「全SKU対象」として扱う）。
      // ファイル経路では併用できないため送らない（バックエンドが 400 を返す）
      target_skus:
        targetSkusFilePath === undefined && parsedSkus.skus.length > 0
          ? parsedSkus.skus
          : undefined,
      target_skus_file_path: targetSkusFilePath,
      target_skus_file_name: targetSkusFilePath !== undefined ? targetSkusFile?.name : undefined,
    };

    onSubmit(selectedAccountCode, request);
  };

  const validDates = targetDates.filter((d) => d !== '');
  const parsedTrainTerms = parseNumberArray(trainTerms);
  const parsedTolerances = parseNumberArray(tolerances);
  const parsedMaxDataCounts = parseNumberArray(maxDataCounts);
  const totalCombinations =
    validDates.length *
    parsedTrainTerms.length *
    parsedTolerances.length *
    parsedMaxDataCounts.length;

  const canSubmit =
    selectedAccountCode &&
    selectedAccountId &&
    validDates.length > 0 &&
    parsedTrainTerms.length > 0 &&
    parsedTolerances.length > 0 &&
    parsedMaxDataCounts.length > 0 &&
    parsedSkus.invalidLines.length === 0 &&
    !isTargetSkusTextOverLimit(parsedSkus) &&
    !isUploading &&
    !isSubmitting;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* アカウント選択 */}
      <div className="space-y-2">
        <Label>アカウント</Label>
        <Select value={selectedAccountCode} onValueChange={handleAccountChange}>
          <SelectTrigger>
            <SelectValue placeholder="アカウントを選択" />
          </SelectTrigger>
          <SelectContent>
            {accounts.map((account) => (
              <SelectItem key={account.id} value={account.code}>
                {account.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* 予測基準日 */}
      <div className="space-y-2">
        <Label>予測基準日 (target_dates)</Label>
        {targetDates.map((date, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              type="date"
              value={date}
              onChange={(e) => updateTargetDate(index, e.target.value)}
            />
            {targetDates.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeTargetDate(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addTargetDate}>
          <Plus className="mr-1 h-4 w-4" />
          日付を追加
        </Button>
      </div>

      {/* 評価期間 */}
      <div className="space-y-2">
        <Label>評価期間 (eval_days)</Label>
        <Input
          type="number"
          value={evalDays}
          onChange={(e) => setEvalDays(Number(e.target.value))}
          min={1}
          max={180}
        />
      </div>

      {/* 対象SKU（テキスト入力と CSV ファイルの排他） */}
      <TargetSkuSection
        text={targetSkusText}
        onTextChange={setTargetSkusText}
        parsed={parsedSkus}
        file={targetSkusFile}
        onFileChange={(file) => {
          setTargetSkusFile(file);
          setUploadError(null);
        }}
        uploadError={uploadError}
        uploadProgress={uploadProgress}
        isUploading={isUploading}
      />

      {/* スイープ対象パラメータ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">スイープ対象パラメータ</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>学習期間 (train_terms) - カンマ区切り</Label>
            <Input
              value={trainTerms}
              onChange={(e) => setTrainTerms(e.target.value)}
              placeholder="14, 21, 28, 35, 56, 90"
            />
          </div>
          <div className="space-y-2">
            <Label>許容欠品率 (tolerances) - カンマ区切り</Label>
            <Input
              value={tolerances}
              onChange={(e) => setTolerances(e.target.value)}
              placeholder="0.4, 0.5"
            />
          </div>
          <div className="space-y-2">
            <Label>使用データ数 (max_data_counts) - カンマ区切り</Label>
            <Input
              value={maxDataCounts}
              onChange={(e) => setMaxDataCounts(e.target.value)}
              placeholder="7, 14"
            />
          </div>
          {totalCombinations > 0 && (
            <p className="text-sm text-muted-foreground">
              合計 {totalCombinations} 組合せ（{validDates.length} 日付 x {parsedTrainTerms.length}{' '}
              train_term x {parsedTolerances.length} tolerance x {parsedMaxDataCounts.length}{' '}
              max_data_count）
            </p>
          )}
        </CardContent>
      </Card>

      {/* 補正パラメータ */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">補正パラメータ</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="adjust_event"
              checked={adjustEvent}
              onCheckedChange={(checked) => setAdjustEvent(checked === true)}
            />
            <Label htmlFor="adjust_event">イベント補正 (adjust_event)</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="adjust_new_skus"
              checked={adjustNewSkus}
              onCheckedChange={(checked) => setAdjustNewSkus(checked === true)}
            />
            <Label htmlFor="adjust_new_skus">新商品SKU補正 (adjust_new_skus)</Label>
          </div>
          {adjustNewSkus && (
            <div className="ml-6 space-y-3">
              <div className="space-y-2">
                <Label>最小学習期間 (adjust_new_skus_min_train_term)</Label>
                <Input
                  type="number"
                  value={adjustNewSkusMinTrainTerm}
                  onChange={(e) => setAdjustNewSkusMinTrainTerm(Number(e.target.value))}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>新商品とみなす販売日数閾値 (adjust_new_skus_max_days_on_sales)</Label>
                <Input
                  type="number"
                  value={adjustNewSkusMaxDaysOnSales}
                  onChange={(e) => setAdjustNewSkusMaxDaysOnSales(Number(e.target.value))}
                  min={1}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ヒストグラムオプション */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">ヒストグラムオプション</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>商品グループタイプ (product_group_type_name)（任意）</Label>
            <Select value={productGroupTypeName} onValueChange={setProductGroupTypeName}>
              <SelectTrigger>
                <SelectValue placeholder="商品グループタイプを選択（任意）" />
              </SelectTrigger>
              <SelectContent>
                {productGroupTypes
                  .filter((t) => t.name)
                  .map((t) => (
                    <SelectItem key={t.id} value={t.name}>
                      {t.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {!selectedAccountCode && (
              <p className="text-sm text-muted-foreground">
                アカウントを選択すると商品グループタイプが表示されます
              </p>
            )}
          </div>
          {productGroupTypeName && (
            <div className="space-y-2">
              <Label>商品グループ階層レベル (product_group_layer)</Label>
              <Select
                value={String(productGroupLayer)}
                onValueChange={(v) => setProductGroupLayer(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      レベル {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* インスタンスタイプ */}
      <div className="space-y-2">
        <Label>インスタンスタイプ</Label>
        <div className="flex items-center gap-2">
          <Select value={instanceType} onValueChange={setInstanceType}>
            <SelectTrigger className="flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INSTANCE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground"
              onClick={() => setQuotaOpen(!quotaOpen)}
            >
              <Info className="h-4 w-4" />
            </button>
            {quotaOpen && (
              <div className="absolute right-0 top-12 z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md">
                <p className="text-sm font-medium mb-2">インスタンス上限（AWSアカウント）</p>
                <div className="space-y-1">
                  {INSTANCE_TYPES.map((t) => (
                    <div key={t.value} className="flex justify-between text-sm">
                      <span className="font-mono">{t.value}</span>
                      <span className="font-semibold">
                        {quotaMap.has(t.value) ? `${quotaMap.get(t.value)}` : '取得中...'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Button type="submit" disabled={!canSubmit} className="w-full">
        <Play className="mr-2 h-4 w-4" />
        {isSubmitting ? '起動中...' : '予測誤差評価を実行'}
      </Button>
    </form>
  );
}
